import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, Printer, User, Car, Wrench, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const STATUS_STYLES = {
  new_request: "bg-slate-500/15 text-slate-300",
  scheduled: "bg-blue-500/15 text-blue-300",
  in_progress: "bg-amber-500/15 text-amber-300",
  calibration_completed: "bg-emerald-500/15 text-emerald-300",
  invoiced: "bg-cyan-500/15 text-cyan-300",
  paid: "bg-emerald-500/15 text-emerald-300",
  closed: "bg-slate-500/15 text-slate-400",
  cancelled: "bg-rose-500/15 text-rose-300",
};

export default function Scheduling() {
  const [date, setDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/jobs").then((r) => setJobs(r.data || [])).finally(() => setLoading(false));
  }, []);

  // Bucket by day. Jobs without scheduled_at fall under today.
  const grouped = useMemo(() => {
    const map = new Map();
    jobs.forEach((j) => {
      let d;
      if (j.scheduled_at) {
        d = new Date(j.scheduled_at);
      } else if (j.status === "new_request" || j.status === "new") {
        d = new Date();  // treat unscheduled new requests as "today - unscheduled"
      } else {
        return;
      }
      const key = d.toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ ...j, _time: d });
    });
    // sort each bucket by time
    map.forEach((arr) => arr.sort((a, b) => a._time - b._time));
    return map;
  }, [jobs]);

  const selectedKey = date.toISOString().slice(0, 10);
  const dayJobs = grouped.get(selectedKey) || [];

  // Calendar decoration — days with jobs
  const modifiers = useMemo(() => {
    const days = [];
    grouped.forEach((_, k) => days.push(new Date(k + "T12:00:00")));
    return { hasJobs: days };
  }, [grouped]);

  const printWorkOrder = async (job) => {
    if (!job.calibration_report_id) {
      toast.error("Este job ainda não tem Work Order gerada. Crie via Orçamento (Fechar) ou New Job Direto.");
      return;
    }
    setPrinting(job.id);
    try {
      const r = await api.get(`/work-orders/${job.calibration_report_id}/bundle.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      // Open in new tab; user prints from browser
      const w = window.open(url, "_blank");
      if (w) w.focus();
      else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `work-order-${job.id.slice(0, 8)}.pdf`;
        a.click();
      }
      toast.success("Work Order aberta — use Ctrl+P para imprimir");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar PDF");
    } finally {
      setPrinting(null);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="scheduling-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Schedule</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Calibration Scheduling</h1>
          <p className="text-sm text-slate-400 mt-1.5">Trabalhos do dia + impressão da Work Order para o técnico.</p>
        </div>
        <div className="rounded-lg border border-slate-800 bg-[#0B1120] px-4 py-2 text-xs text-slate-400 flex items-center gap-2">
          <CalendarDays className="w-3.5 h-3.5 text-blue-400" />
          <span>{jobs.length} total · {dayJobs.length} neste dia</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            modifiers={modifiers}
            modifiersClassNames={{ hasJobs: "bg-blue-500/10 text-blue-300 font-semibold ring-1 ring-blue-500/30" }}
            className="bg-transparent"
          />
          <div className="mt-3 pt-3 border-t border-slate-800 text-[11px] text-slate-500">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-400 mr-1.5"></span>
            Dias com trabalhos agendados
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-blue-400" />
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Trabalhos do dia</div>
            </div>
            <h3 className="font-display text-xl font-medium">{date?.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</h3>
          </div>

          {loading && (
            <div className="text-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          )}
          {!loading && dayJobs.length === 0 && (
            <div className="text-center py-10 text-slate-500 border border-dashed border-slate-800 rounded-lg">
              Nenhum trabalho para este dia.
              {isSameDay(date, new Date()) && (
                <div className="text-xs mt-2">Crie um <Link to="/jobs/new-direct" className="text-blue-300 hover:underline">Job Direto</Link> ou feche um <Link to="/quotes" className="text-blue-300 hover:underline">Orçamento</Link>.</div>
              )}
            </div>
          )}
          <div className="space-y-2">
            {dayJobs.map((j) => {
              const time = j.scheduled_at
                ? new Date(j.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : "—";
              return (
                <div key={j.id} data-testid={`schedule-job-${j.id}`} className="flex items-center gap-4 p-3 rounded-lg bg-slate-900/40 border border-slate-800/60 hover:border-blue-500/40 transition-colors">
                  <div className="text-center w-16 shrink-0">
                    <div className="text-lg font-semibold text-blue-300 font-mono">{time}</div>
                    {j.status && <span className={`inline-block mt-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${STATUS_STYLES[j.status] || "bg-slate-500/15 text-slate-300"}`}>{j.status.replace(/_/g, " ")}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      <Car className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      {j.vehicle_year} {j.vehicle_make} {j.vehicle_model}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{j.customer_name || "—"}</span>
                      <span className="font-mono">{(j.vin || "").slice(-8)}</span>
                      {j.body_shop_name && <span className="text-blue-300">· {j.body_shop_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link to={`/jobs/${j.id}`}>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-blue-300" data-testid={`schedule-view-${j.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                      onClick={() => printWorkOrder(j)}
                      disabled={printing === j.id || !j.calibration_report_id}
                      data-testid={`schedule-print-wo-${j.id}`}
                      title={j.calibration_report_id ? "Imprimir Work Order" : "Sem Work Order gerada — feche o orçamento ou use New Job Direto"}
                    >
                      {printing === j.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Printer className="w-3.5 h-3.5 mr-1" />}
                      WO
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
